import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
const API = `${BACKEND_URL}/api`;

export const resolveImageUrl = (url) => {
  if (!url) return "";
  if (url.startsWith("/uploads/")) return `${BACKEND_URL}${url}`;
  return url;
};

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("fp_token"));
  const [loading, setLoading] = useState(true);

  const logout = useCallback(() => {
    localStorage.removeItem("fp_token");
    setToken(null);
    setUser(null);
  }, []);

  // Verify token on mount / token change
  useEffect(() => {
    if (token) {
      axios
        .get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then((res) => setUser(res.data))
        .catch((err) => {
          // Token expiré ou invalide → déconnexion silencieuse
          if (err.response?.status === 401) logout();
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [token, logout]);

  const login = async (username, password) => {
    const res = await axios.post(`${API}/auth/login`, { username, password });
    const { token: newToken, user: newUser } = res.data;
    localStorage.setItem("fp_token", newToken);
    setToken(newToken);
    setUser(newUser);
    return res.data;
  };

  const register = async (username, password) => {
    const res = await axios.post(`${API}/auth/register`, { username, password });
    const { token: newToken, user: newUser } = res.data;
    localStorage.setItem("fp_token", newToken);
    setToken(newToken);
    setUser(newUser);
    return res.data;
  };

  // Axios instance — intercepte automatiquement les 401 (token expiré en cours de session)
  const authAxios = axios.create({
    baseURL: API,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  authAxios.interceptors.response.use(
    (res) => res,
    (err) => {
      if (err.response?.status === 401) logout();
      return Promise.reject(err);
    }
  );

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, api: authAxios }}>
      {children}
    </AuthContext.Provider>
  );
};
